import { AvatarColor } from '@components/avatar';
import { StatusLineProps } from '@components/status-line';
import { abbreviateDate } from '@utils/date';

import { IApplication } from 'models/types';
import type { ApplicationStatus } from 'types/applications';

type ApplicationOutcome = {
  isHired: boolean;
  isRejected: boolean;
  isRetracted: boolean;
};

type StatusStamp = {
  actor?: { name?: string | null } | null;
  date?: string | null;
};

type StatusDetailArgs = {
  application: IApplication;
  status: ApplicationStatus;
  lang: 'is' | 'en';
  t: (key: string, ...args: Array<string>) => string;
};

// The detail page serializer does not expose `status`, so derive it from the
// booleans instead. Precedence matches the backend `status` property.
export function getApplicationStatus(application: ApplicationOutcome): ApplicationStatus {
  if (application.isHired) return 'hired';
  if (application.isRejected) return 'rejected';
  if (application.isRetracted) return 'retracted';
  return 'active';
}

const statusLineVariants: {
  [status in ApplicationStatus]: StatusLineProps['variant'] | undefined;
} = {
  active: undefined,
  hired: 'green',
  rejected: 'red',
  retracted: 'orange',
};

// Shares the precedence above, so hired-and-retracted draws one bar not two.
export function getStatusLineVariant(
  application: IApplication
): StatusLineProps['variant'] | undefined {
  return statusLineVariants[getApplicationStatus(application)];
}

const avatarColors: { [status in ApplicationStatus]: AvatarColor } = {
  active: 'purple',
  hired: 'green',
  rejected: 'red',
  retracted: 'orange',
};

export function getAvatarColor(application: ApplicationOutcome): AvatarColor {
  return avatarColors[getApplicationStatus(application)];
}

/** `ApplicationSearchList`, which the Applicants page reads, omits `is_hired` and
 * `is_retracted`, so the status cannot be derived there. */
export function getAvatarColorForStatus(status: ApplicationStatus): AvatarColor {
  return avatarColors[status];
}

// Accessors rather than field-name strings, so a rename on the model is a
// compile error here instead of a silently empty row.
const statusStamps: {
  [status in ApplicationStatus]: ((application: IApplication) => StatusStamp) | null;
} = {
  active: null,
  hired: (application) => {
    return { actor: application.hiredByUser, date: application.dateOfHire };
  },
  rejected: (application) => {
    return { actor: application.rejectedByUser, date: application.dateOfRejection };
  },
  retracted: (application) => {
    return { actor: application.retractedByUser, date: application.dateOfRetraction };
  },
};

export function getStatusDetail({
  application,
  status,
  lang,
  t,
}: StatusDetailArgs): string | undefined {
  const stamp = statusStamps[status]?.(application);
  if (!stamp) return undefined;

  const actorName = stamp.actor?.name;
  const date = abbreviateDate(stamp.date, lang);

  // Retractions predating the columns resolve to neither, so the status renders
  // on its own rather than with a half-empty detail.
  if (actorName && date) return t('application.header.by', actorName, date);
  return date || undefined;
}
